/**
 * Dependency-free image compression for bulk uploads — scales an image down to
 * fit within MAX_DIM on its longest side and re-encodes it as a JPEG. Used by
 * the multi-image picker (the single-image path keeps using ImageEditorDialog
 * for crop/rotate). Aspect ratio is preserved; gallery thumbnails crop to a
 * square via CSS object-cover.
 */
const MAX_DIM = 1024;
const QUALITY = 0.82;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('image decode failed'));
    im.src = url;
  });
}

export async function compressImageFile(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const { naturalWidth: w, naturalHeight: h } = img;
    const scale = Math.min(1, MAX_DIM / Math.max(w, h || 1));
    const outW = Math.max(1, Math.round(w * scale));
    const outH = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // White matte — JPEG has no alpha so transparent PNGs flatten cleanly.
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(img, 0, 0, outW, outH);
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob(res, 'image/jpeg', QUALITY),
    );
    return blob ?? file;
  } catch {
    return file; // fall back to the original bytes on any decode/encode error
  } finally {
    URL.revokeObjectURL(url);
  }
}
